import * as React from "react";
import {
  Bell,
  Check,
  CheckCheck,
  CalendarClock,
  ExternalLink,
  Wrench,
  CreditCard,
  FileText,
  CheckCircle2,
  ClipboardList,
  MessageCircle,
  Inbox,
  X,
} from "lucide-react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as SheetPrimitive from "@radix-ui/react-dialog";

import { cn } from "../lib/utils";

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return isMobile;
}

function formatRelativeTime(timestamp, locale = "en") {
  if (!timestamp) return "";
  try {
    const date =
      typeof timestamp === "number"
        ? new Date(timestamp)
        : new Date(String(timestamp));
    if (isNaN(date.getTime())) return String(timestamp);

    const now = Date.now();
    const diffSec = Math.floor((now - date.getTime()) / 1000);

    if (typeof Intl !== "undefined" && Intl.RelativeTimeFormat) {
      const rtf = new Intl.RelativeTimeFormat(locale, {
        numeric: "auto",
        style: "narrow",
      });
      if (Math.abs(diffSec) < 60) {
        return rtf.format(-Math.max(0, diffSec), "second");
      }
      const diffMin = Math.floor(diffSec / 60);
      if (Math.abs(diffMin) < 60) {
        return rtf.format(-diffMin, "minute");
      }
      const diffHours = Math.floor(diffMin / 60);
      if (Math.abs(diffHours) < 24) {
        return rtf.format(-diffHours, "hour");
      }
      const diffDays = Math.floor(diffHours / 24);
      if (Math.abs(diffDays) < 7) {
        return rtf.format(-diffDays, "day");
      }
    }

    return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

function getTypeIcon(rawType) {
  const type = String(rawType || "").toLowerCase();
  if (type.includes("maint") || type.includes("work_order")) {
    return <Wrench className="size-3.5 text-warning" />;
  }
  if (type.includes("pay") || type.includes("invoice")) {
    return <CreditCard className="size-3.5 text-success" />;
  }
  if (type.includes("app")) {
    return <ClipboardList className="size-3.5 text-info" />;
  }
  if (type.includes("contract") || type.includes("lease") || type.includes("sign")) {
    return <FileText className="size-3.5 text-info" />;
  }
  if (type.includes("visit") || type.includes("calendar") || type.includes("booking")) {
    return <CalendarClock className="size-3.5 text-primary" />;
  }
  if (type.includes("support") || type.includes("comment")) {
    return <MessageCircle className="size-3.5 text-primary" />;
  }
  if (type.includes("task")) {
    return <CheckCircle2 className="size-3.5 text-destructive" />;
  }
  return <Inbox className="size-3.5 text-muted-foreground" />;
}

const NotificationPopover = React.forwardRef(
  (
    {
      notifications = [],
      items: rawItems,
      unreadCount: explicitUnreadCount,
      onMarkAsRead,
      onMarkAllAsRead,
      onOpenItem,
      onItemClick,
      onViewAll,
      viewAllHref = "/inbox",
      align = "end",
      locale = "en",
      titleLabel = "Notifications",
      markAllReadLabel = "Mark all read",
      markReadLabel = "Mark as read",
      allTabLabel = "All",
      unreadTabLabel = "Unread",
      emptyTitle = "All caught up",
      emptyUnreadSubtitle = "No unread notifications",
      emptyAllSubtitle = "No recent notifications",
      viewAllLabel = "View all in Operations Inbox",
      closeLabel = "Close",
      className,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const [open, setOpen] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState("all"); // "all" | "unread"

    const notificationItems = rawItems || notifications;

    const unreadCount =
      explicitUnreadCount !== undefined
        ? explicitUnreadCount
        : notificationItems.filter((item) => !item.read && !item.isRead).length;

    const filteredItems = React.useMemo(() => {
      if (activeTab === "unread") {
        return notificationItems.filter((item) => !item.read && !item.isRead);
      }
      return notificationItems;
    }, [notificationItems, activeTab]);

    const handleItemClick = (item) => {
      const isUnread = !item.read && !item.isRead;
      if (isUnread) {
        onMarkAsRead?.(item.id);
      }
      const openHandler = onOpenItem || onItemClick;
      if (openHandler) {
        openHandler(item);
      } else if (item.linkUrl) {
        window.location.href = item.linkUrl;
      }
      setOpen(false);
    };

    const triggerButton = (
      <button
        ref={ref}
        type="button"
        aria-label={titleLabel}
        data-testid="notification-popover-trigger"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          "relative flex size-10 items-center justify-center rounded-xl border border-border/80 bg-muted/20 text-foreground transition-colors hover:bg-muted/50 data-[state=open]:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer min-h-[44px] min-w-[44px]",
          className
        )}
        {...props}
      >
        <Bell className="size-4" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-unread-dot"
            className="absolute -top-0.5 -right-0.5 flex size-3"
          >
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-75" />
            <span className="relative inline-flex size-3 rounded-full bg-success ring-2 ring-card" />
          </span>
        )}
      </button>
    );

    const contentBody = (
      <div
        className="flex flex-col max-h-[460px]"
        data-testid="notification-popover-content"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground tracking-tight">
              {titleLabel}
            </h3>
            {unreadCount > 0 && (
              <span
                data-testid="notification-unread-badge"
                className="rounded-full bg-success/15 px-2 py-0.5 text-caption-size font-semibold font-mono text-success border border-success/30 tabular-nums"
              >
                {unreadCount}
              </span>
            )}
          </div>
          {unreadCount > 0 && onMarkAllAsRead && (
            <button
              type="button"
              data-testid="notification-mark-all-read"
              onClick={() => onMarkAllAsRead()}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <CheckCheck className="size-3.5 text-success" />
              <span>{markAllReadLabel}</span>
            </button>
          )}
        </div>

        {/* Tab Controls */}
        <div
          role="tablist"
          aria-orientation="horizontal"
          className="flex border-b border-border px-4 bg-muted/30"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "all"}
            aria-controls="notification-feed-list"
            data-testid="notification-tab-all"
            onClick={() => setActiveTab("all")}
            className={cn(
              "px-3 py-2 text-xs font-medium border-b-2 transition-colors cursor-pointer",
              activeTab === "all"
                ? "border-primary text-foreground font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            {allTabLabel}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "unread"}
            aria-controls="notification-feed-list"
            data-testid="notification-tab-unread"
            onClick={() => setActiveTab("unread")}
            className={cn(
              "px-3 py-2 text-xs font-medium border-b-2 transition-colors cursor-pointer flex items-center gap-1.5",
              activeTab === "unread"
                ? "border-primary text-foreground font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <span>{unreadTabLabel}</span>
            {unreadCount > 0 && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-overline-size font-mono tabular-nums text-foreground">
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Feed List */}
        <div
          id="notification-feed-list"
          role="tabpanel"
          className="flex-1 overflow-y-auto divide-y divide-border/60"
        >
          {filteredItems.length === 0 ? (
            <div
              data-testid="notification-empty-state"
              className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground"
            >
              <Inbox className="size-8 opacity-40 mb-2" />
              <p className="text-xs font-medium text-foreground">{emptyTitle}</p>
              <p className="text-caption text-muted-foreground mt-0.5">
                {activeTab === "unread" ? emptyUnreadSubtitle : emptyAllSubtitle}
              </p>
            </div>
          ) : (
            filteredItems.map((item) => {
              const isUnread = !item.read && !item.isRead;
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  data-testid="notification-item"
                  onClick={() => handleItemClick(item)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleItemClick(item);
                    }
                  }}
                  className={cn(
                    "group flex items-start gap-3 p-3.5 transition-colors cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-inset",
                    isUnread
                      ? "bg-primary/5 hover:bg-primary/10"
                      : "hover:bg-muted/40"
                  )}
                >
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted border border-border mt-0.5">
                    {getTypeIcon(item.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className={cn(
                          "truncate text-xs text-foreground leading-tight",
                          isUnread ? "font-semibold" : "font-normal"
                        )}
                      >
                        {item.title}
                      </p>
                      <span className="shrink-0 text-overline-size text-muted-foreground font-mono tabular-nums">
                        {formatRelativeTime(
                          item.timestamp || item.createdAt,
                          locale
                        )}
                      </span>
                    </div>

                    {item.description && (
                      <p className="text-caption text-muted-foreground line-clamp-2 mt-0.5">
                        {item.description}
                      </p>
                    )}
                  </div>

                  {isUnread && (
                    <div className="flex items-center gap-1 shrink-0 mt-0.5">
                      {onMarkAsRead && (
                        <button
                          type="button"
                          title={markReadLabel}
                          aria-label={markReadLabel}
                          data-testid="notification-mark-read-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onMarkAsRead(item.id);
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                          }}
                          className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-success transition-all cursor-pointer"
                        >
                          <Check className="size-4" />
                        </button>
                      )}
                      <span className="size-2 rounded-full bg-success shrink-0" />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer Deep Link */}
        <div className="border-t border-border bg-muted/20 p-2.5 text-center">
          <button
            type="button"
            data-testid="notification-view-all"
            onClick={() => {
              setOpen(false);
              if (onViewAll) onViewAll();
              else if (viewAllHref) window.location.href = viewAllHref;
            }}
            className="inline-flex items-center justify-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary transition-colors cursor-pointer"
          >
            <span>{viewAllLabel}</span>
            <ExternalLink className="size-3" />
          </button>
        </div>
      </div>
    );

    if (isMobile) {
      return (
        <SheetPrimitive.Root open={open} onOpenChange={setOpen}>
          <SheetPrimitive.Trigger asChild>{triggerButton}</SheetPrimitive.Trigger>
          <SheetPrimitive.Portal>
            <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
            <SheetPrimitive.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col rounded-t-modal border-t border-border bg-popover text-popover-foreground shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom">
              <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-muted-foreground/30" />
              <SheetPrimitive.Close className="absolute right-4 top-3 flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer">
                <X className="size-4" />
                <span className="sr-only">{closeLabel}</span>
              </SheetPrimitive.Close>
              {contentBody}
            </SheetPrimitive.Content>
          </SheetPrimitive.Portal>
        </SheetPrimitive.Root>
      );
    }

    return (
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger asChild>{triggerButton}</PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align={align}
            sideOffset={8}
            className="z-50 w-96 overflow-hidden rounded-2xl border border-border bg-popover p-0 text-popover-foreground shadow-2xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          >
            {contentBody}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }
);
NotificationPopover.displayName = "NotificationPopover";

export { NotificationPopover };
